import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { 
  Users, 
  Plus, 
  Search, 
  Filter, 
  MoreHorizontal,
  Edit,
  Eye,
  Trash2,
  CheckCircle,
  XCircle,
  Calendar,
  ChevronDown,
  ArrowUpRight,
  Building,
  Globe,
  Clock,
  TrendingUp,
  TrendingDown
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { formatDate, formatRelativeTime } from '../../utils/format'
import { cn } from '../../utils/cn'

const ClientManager = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [selectedClients, setSelectedClients] = useState([])
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [clientToDelete, setClientToDelete] = useState(null)
  const hasInitiallyAnimated = React.useRef(false)

  const queryClient = useQueryClient()

  // Mark that initial animation has occurred
  React.useEffect(() => {
    hasInitiallyAnimated.current = true
  }, [])

  // Fetch clients
  const { data: clientsData, isLoading, error } = useQuery(
    ['clients'],
    () => apiService.get('/clients').then(res => res.data),
    { staleTime: 30000 }
  )
  const clients = clientsData?.clients || []

  // Fetch plans to resolve custom plan names
  const { data: plansRaw } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(r => r.data),
    { staleTime: 5 * 60 * 1000 }
  )

  // Mock data for demo
  const mockClients = [
    {
      id: 1,
      user_name: 'John Doe',
      email: 'john@acme.com',
      company_name: 'Acme Corporation',
      subscription_plan: 'pro',
      site_count: 3,
      edits_used: 15,
      edit_quota: 25,
      last_login: '2024-01-15T10:30:00Z',
      user_created_at: '2024-01-01T00:00:00Z',
      is_active: true
    },
    {
      id: 2,
      user_name: 'Jane Smith',
      email: 'jane@techsolutions.com',
      company_name: 'Tech Solutions Inc',
      subscription_plan: 'basic',
      site_count: 1,
      edits_used: 5,
      edit_quota: 10,
      last_login: '2024-01-14T15:20:00Z',
      user_created_at: '2024-01-10T00:00:00Z',
      is_active: true
    },
    {
      id: 3,
      user_name: 'Bob Wilson',
      email: 'bob@designco.com',
      company_name: 'Design Co',
      subscription_plan: 'premium',
      site_count: 5,
      edits_used: 42,
      edit_quota: 50,
      last_login: '2024-01-12T14:15:00Z',
      user_created_at: '2023-12-15T00:00:00Z',
      is_active: false
    }
  ]

  // Delete client mutation
  const deleteClientMutation = useMutation(
    (clientId) => apiService.delete(`/clients/${clientId}`),
    {
      onSuccess: () => {
        toast.success('Client deleted successfully')
        queryClient.invalidateQueries(['clients'])
        queryClient.invalidateQueries(['admin-stats'])
        queryClient.invalidateQueries('admin-quick-stats')
        queryClient.invalidateQueries(['analytics'])
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to delete client')
      }
    }
  )

  const handleDeleteClient = (client) => {
    setClientToDelete(client)
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = () => {
    if (clientToDelete) {
      deleteClientMutation.mutate(clientToDelete.id)
      setShowDeleteConfirm(false)
      setClientToDelete(null)
    }
  }

  // Handle click outside modal to close
  React.useEffect(() => {
    const handleModalClickOutside = (event) => {
      if (showDeleteConfirm && !event.target.closest('.modal-content')) {
        setShowDeleteConfirm(false)
        setClientToDelete(null)
      }
    }
    
    if (showDeleteConfirm) {
      document.addEventListener('mousedown', handleModalClickOutside)
      return () => document.removeEventListener('mousedown', handleModalClickOutside)
    }
  }, [showDeleteConfirm])

  // Use real data if available, fallback to mock data if API fails
  const allClients = clients || (error ? mockClients : [])
  
  // Filter clients based on search query and plan status
  const displayClients = allClients.filter(client => {
    const matchesSearch = !searchQuery || 
      client.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (client.company_name && client.company_name.toLowerCase().includes(searchQuery.toLowerCase()))
    
    const matchesStatus = !filterStatus || client.subscription_plan === filterStatus
    
    return matchesSearch && matchesStatus
  })

  const StatusBadge = ({ plan }) => {
    const isCustom = plansRaw?.[plan]?.custom === true
    const colors = {
      basic:      'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200',
      pro:        'bg-accent-100 text-accent-700 dark:bg-accent-900 dark:text-accent-200',
      enterprise: 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-200',
    }
    return (
      <span className={cn(
        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap',
        isCustom
          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
          : colors[plan] || 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-200'
      )}>
        {isCustom ? 'CUSTOM' : (plan?.toUpperCase() || 'BASIC')}
      </span>
    )
  }

  const BillingStatusBadge = ({ status, plan }) => {
    const isFreePlan = plansRaw?.[plan]?.price === 0
    const effectiveStatus = isFreePlan ? 'active' : status
    const map = {
      active:   { cls: 'bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-200', label: 'Active' },
      trialing: { cls: 'bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-200', label: 'Trialing' },
      past_due: { cls: 'bg-error-100 text-error-700 dark:bg-error-900 dark:text-error-200',         label: 'Past due' },
      unpaid:   { cls: 'bg-error-100 text-error-700 dark:bg-error-900 dark:text-error-200',         label: 'Unpaid' },
    }
    const { cls, label } = map[effectiveStatus] || { cls: 'bg-warning-100 text-warning-700 dark:bg-warning-900 dark:text-warning-200', label: 'Pending' }
    return (
      <span className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap', cls)}>
        {label}
      </span>
    )
  }

  const QuotaBar = ({ used, total }) => {
    const percentage = (used / total) * 100
    const isNearLimit = percentage > 80
    const isOverLimit = percentage > 100

    return (
      <div className="w-full">
        <div className="w-full bg-neutral-200 dark:bg-[#2f2b41] rounded-full h-2">
          <div
            className={cn(
              'h-2 rounded-full transition-all duration-300',
              isOverLimit ? 'bg-error-500' : isNearLimit ? 'bg-warning-500' : 'bg-success-500'
            )}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
      </div>
    )
  }

  const StatCard = ({ title, value, icon: Icon, change, changeType = 'positive', isLoading = false, index = 0 }) => (
    <motion.div
      initial={hasInitiallyAnimated.current ? false : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: hasInitiallyAnimated.current ? 0 : index * 0.1 }}
    >
      <Card className="hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-neutral-50 dark:from-dark-200 dark:to-dark-300 border border-neutral-200 dark:border-neutral-700 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                {title}
              </p>
              {isLoading ? (
                <div className="animate-pulse bg-neutral-300 dark:bg-neutral-600 h-8 w-20 rounded"></div>
              ) : (
                <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">
                  {value?.toLocaleString() || 0}
                </p>
              )}
            </div>
            <div className="h-12 w-12 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl flex items-center justify-center shadow-lg">
              <Icon className="h-6 w-6 text-white" />
            </div>
          </div>
          {change !== undefined && !isLoading && (
            <div className="mt-4 flex items-center space-x-1">
              {changeType === 'positive' ? (
                <TrendingUp className="h-4 w-4 text-success-500" />
              ) : (
                <TrendingDown className="h-4 w-4 text-error-500" />
              )}
              <span className={cn(
                'text-sm font-medium',
                changeType === 'positive' ? 'text-success-600' : 'text-error-600'
              )}>
                {Math.abs(change)}%
              </span>
              <span className="text-sm text-neutral-500">vs last month</span>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  )

  const ClientCard = ({ client, index }) => (
    <motion.div
      initial={hasInitiallyAnimated.current ? false : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: hasInitiallyAnimated.current ? 0 : index * 0.1 }}
      className="h-full"
    >
      <Card className="hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-neutral-50 dark:from-dark-200 dark:to-dark-300 border border-neutral-200 dark:border-neutral-700 shadow-sm h-full">
        <CardContent className="p-6 h-full flex flex-col">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4 min-w-0 flex-1">
              <div className="flex-shrink-0 h-12 w-12 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-lg">
                  {client.user_name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 truncate">
                  {client.user_name}
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 truncate">
                  {client.email}
                </p>
                <div className="flex items-center space-x-1 mt-1 min-h-[1.25rem]">
                  <Building className="h-3 w-3 text-neutral-500 flex-shrink-0" />
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                    {client.company_name || ''}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end space-y-2 flex-shrink-0 ml-3">
              <BillingStatusBadge status={client.billing_status} plan={client.subscription_plan} />
              <StatusBadge plan={client.subscription_plan} />
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Globe className="h-4 w-4 text-neutral-500" />
                <span className="text-sm text-neutral-600 dark:text-neutral-400">Sites</span>
              </div>
              <p className="text-xl font-bold text-neutral-900 dark:text-neutral-50">
                {client.site_count}
              </p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-neutral-500" />
                <span className="text-sm text-neutral-600 dark:text-neutral-400">Last Login</span>
              </div>
              <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {client.last_login ? formatRelativeTime(client.last_login) : 'Never'}
              </p>
            </div>
          </div>

          <div className="mt-4 mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-neutral-600 dark:text-neutral-400">Edit Quota</span>
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {client.edits_used || 0}/{client.edit_quota || 10}
              </span>
            </div>
            <QuotaBar used={client.edits_used || 0} total={client.edit_quota || 10} />
          </div>

          <div className="mt-12 flex justify-end space-x-2 mt-auto">
            <Button variant="ghost" size="sm" asChild>
              <Link to={`/admin/clients/${client.id}`}>
                <Eye className="h-4 w-4 mr-2" />
                View
              </Link>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <Link to={`/admin/clients/${client.id}/edit`}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Link>
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => handleDeleteClient(client)}
              disabled={deleteClientMutation.isLoading}
              className="text-error-600 hover:text-error-700 hover:bg-error-50 dark:text-error-400 dark:hover:text-error-300 dark:hover:bg-error-900/20"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )


  return (
    <div className="space-y-8 pt-4">
      {/* Header */}
      <motion.div
        initial={hasInitiallyAnimated.current ? false : { opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">
            Client Management
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Manage your clients and their subscriptions
          </p>
        </div>
        
        <Button asChild>
          <Link to="/admin/clients/new">
            <Plus className="h-4 w-4 mr-2" />
            Add Client
          </Link>
        </Button>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={hasInitiallyAnimated.current ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: hasInitiallyAnimated.current ? 0 : 0.1 }}
        className="flex flex-col sm:flex-row gap-4"
      >
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-10 w-full pl-10 pr-4 border border-neutral-200 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-colors"
          />
        </div>

        <div className="relative">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="h-10 pl-3 pr-10 bg-white dark:bg-dark-300 border border-neutral-300 dark:border-neutral-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent-600 focus:border-transparent dark:text-neutral-100 transition-colors appearance-none cursor-pointer"
          >
            <option value="">All plans</option>
            <option value="basic">Basic</option>
            <option value="pro">Pro</option>
            <option value="premium">Premium</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-500 dark:text-neutral-400 pointer-events-none" />
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Clients"
          value={displayClients.length}
          icon={Users}
          index={0}
        />
        <StatCard
          title="Active Clients"
          value={displayClients.filter(c => c.is_active).length}
          icon={CheckCircle}
          index={1}
        />
        <StatCard
          title="This Month"
          value={displayClients.filter(c => {
            const createdDate = new Date(c.user_created_at)
            const now = new Date()
            const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1)
            return createdDate >= thisMonth
          }).length}
          icon={Calendar}
          index={2}
        />
        <StatCard
          title="Inactive"
          value={displayClients.filter(c => !c.is_active).length}
          icon={XCircle}
          index={3}
        />
      </div>

      {/* Clients Grid */}
      {displayClients.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {displayClients.map((client, index) => (
            <ClientCard key={client.id} client={client} index={index} />
          ))}
        </div>
      ) : (
        <div>
          <Card className="border-2 border-dashed border-neutral-300 dark:border-neutral-600 bg-neutral-50 dark:bg-dark-300">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="mx-auto w-16 h-16 bg-gradient-to-br from-accent-100 to-accent-200 dark:from-accent-800 dark:to-accent-900 rounded-full flex items-center justify-center mb-6">
                <Users className="h-8 w-8 text-accent-600 dark:text-accent-400" />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 mb-2">
                No clients found
              </h3>
              <p className="text-neutral-600 dark:text-neutral-400 text-center max-w-md mb-8">
                {searchQuery || filterStatus 
                  ? "No clients match your search criteria. Try adjusting your filters."
                  : "Get started by adding your first client to the platform."
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild>
                  <Link to="/admin/clients/new">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Client
                  </Link>
                </Button>
                {(searchQuery || filterStatus) && (
                  <Button variant="outline" onClick={() => {
                    setSearchQuery('')
                    setFilterStatus('')
                  }}>
                    Clear Filters
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && clientToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="modal-content bg-white dark:bg-[#221f2f] rounded-lg p-6 max-w-md w-full mx-4 border border-neutral-200 dark:border-[#2f2b41]">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-error-100 dark:bg-error-900/20 rounded-full">
              <Trash2 className="w-6 h-6 text-error-600 dark:text-error-400" />
            </div>
            
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                Delete Client
              </h3>
              <p className="text-neutral-600 dark:text-neutral-400">
                Are you sure you want to delete <span className="font-medium text-neutral-900 dark:text-neutral-100">{clientToDelete.company_name}</span>? 
                This action cannot be undone and will delete all associated sites and data.
              </p>
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteConfirm(false)
                  setClientToDelete(null)
                }}
                disabled={deleteClientMutation.isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteConfirm}
                disabled={deleteClientMutation.isLoading}
              >
                {deleteClientMutation.isLoading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Client
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClientManager